import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  AlertTriangle,
  BookOpen,
  FileText,
  Filter,
  GitCompareArrows,
  Info,
  ListChecks,
  MessageSquare,
  Rocket,
  Send,
  Sparkles,
  Upload,
  Wand2,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// The guide is split into TWO top-level parts:
//
//   • QUICK START — the linear happy path, in three stages:
//        1. Prepare data   (Upload / Synthesize / Try sample data)
//        2. Generate letter
//        3. Refine letter
//     Preparing data always ends the same way: a claim doc + a denial doc in
//     the session. Picking the appeal type is NOT a separate stage — it is
//     how the denial (835 / EOB) gets created, so it lives inside the Upload
//     and Synthesize flows.
//   • REFERENCE — per-panel feature docs. Browse in any order; NOT a sequence.
//
// Layout follows Microsoft Learn / Azure docs conventions: single column,
// numbered procedures, ONE screenshot inline directly under the step it
// illustrates (never a row of thumbnails), shown at readable column width
// with a border and click-to-zoom. Only steps that need a visual carry one.
// ---------------------------------------------------------------------------

type Figure = { src: string; alt: string; caption?: string };
type NavItem = {
  id: string;
  title: string;
  icon: typeof BookOpen;
  children?: NavItem[];
};
// A procedure step: instruction text plus, optionally, the screenshot that
// illustrates exactly this step.
type Step = { text: ReactNode; figure?: Figure };

// ---- Quick start: 1. Prepare data -----------------------------------------

type PrepMethod = {
  id: string;
  title: string;
  icon: typeof BookOpen;
  tagline: string;
  steps: Step[];
};

const PREPARE_METHODS: PrepMethod[] = [
  {
    id: "prepare-upload",
    title: "Upload data",
    icon: Upload,
    tagline: "Bring your own real files; fabricate only what's missing.",
    steps: [
      {
        text: (
          <>
            On the empty state, <strong>Browse 837 / UB04</strong> and pick a
            real file. The first document must be an{" "}
            <strong>837 or UB04</strong>.
          </>
        ),
        figure: {
          src: "/guide/up-click.png",
          alt: "Screenshot that shows the empty state with the Browse 837 and Browse UB04 buttons highlighted.",
          caption: "The two Browse buttons on the empty state.",
        },
      },
      {
        text: (
          <>
            Upload any other <strong>real</strong> documents you have{" "}
            <em>now</em> — before synthesizing anything, since the first synth
            locks uploads.
          </>
        ),
      },
      {
        text: (
          <>
            You still need a <strong>denial</strong>. Open the{" "}
            <strong>Documents</strong> tab and click{" "}
            <strong>🪄 Synthesize</strong> on the <strong>835</strong> or{" "}
            <strong>EOB</strong> row.
          </>
        ),
        figure: {
          src: "/guide/doc-synth-denial.png",
          alt: "Screenshot that shows the Documents tab with the Synthesize icon on the 835 row highlighted.",
          caption: "Synthesize the 835 (or EOB) row in the Documents tab.",
        },
      },
      {
        text: (
          <>
            That click opens the <strong>appeal-type picker</strong> — choose
            your type(s) and <strong>Generate</strong>. You now have a claim and
            a denial.
          </>
        ),
        figure: {
          src: "/guide/02-scenario.png",
          alt: "Screenshot that shows the appeal-type picker that opens when you synthesize a denial document.",
          caption: "Pick the appeal type, then Generate.",
        },
      },
    ],
  },
  {
    id: "prepare-synth",
    title: "Synthesize data",
    icon: Wand2,
    tagline: "Fabricate the whole claim — no files needed.",
    steps: [
      {
        text: (
          <>
            On the empty state, click <strong>Generate synthetic claim</strong>{" "}
            — a one-click 837. (This alone does <em>not</em> lock an appeal
            type.)
          </>
        ),
        figure: {
          src: "/guide/synth-click.png",
          alt: "Screenshot that shows the empty state with the Generate synthetic claim button highlighted.",
          caption: "Generate synthetic claim on the empty state.",
        },
      },
      {
        text: (
          <>
            Open the <strong>Documents</strong> tab and click{" "}
            <strong>🪄 Synthesize</strong> on the <strong>835</strong> or{" "}
            <strong>EOB</strong> row to add the denial.
          </>
        ),
        figure: {
          src: "/guide/doc-synth-denial.png",
          alt: "Screenshot that shows the Documents tab with the Synthesize icon on the 835 row highlighted.",
          caption: "Synthesize the 835 (or EOB) row in the Documents tab.",
        },
      },
      {
        text: (
          <>
            The <strong>appeal-type picker</strong> opens — choose type(s) and{" "}
            <strong>Generate</strong>. This locks the appeal type and creates
            the denial.
          </>
        ),
        figure: {
          src: "/guide/02-scenario.png",
          alt: "Screenshot that shows the appeal-type picker that opens when you synthesize a denial document.",
          caption: "Pick the appeal type, then Generate.",
        },
      },
    ],
  },
  {
    id: "prepare-sample",
    title: "Try sample data",
    icon: Rocket,
    tagline: "Fastest — claim, denial and supporting docs in one click.",
    steps: [
      {
        text: (
          <>
            On the empty state, click <strong>Try sample data</strong>.
          </>
        ),
        figure: {
          src: "/guide/sample-click.png",
          alt: "Screenshot that shows the empty state with the Try sample data button highlighted.",
          caption: "Try sample data on the empty state.",
        },
      },
      {
        text: (
          <>
            In the picker, tick appeal type(s) and click{" "}
            <strong>Generate sample bundle</strong>.
          </>
        ),
        figure: {
          src: "/guide/qs1-result.png",
          alt: "Screenshot that shows the appeal-type picker in bundle mode with Generate sample bundle.",
          caption: "Pick types, then Generate sample bundle.",
        },
      },
      {
        text: (
          <>
            You get the full <strong>6-doc bundle</strong> (837, 835, UB04, EOB,
            Medical Record, Letterhead) — claim, denial and support at once.
          </>
        ),
        figure: {
          src: "/guide/03-documents.png",
          alt: "Screenshot that shows the Documents rail populated with the full synthetic bundle.",
          caption: "The full bundle lands in the document rail.",
        },
      },
    ],
  },
];

// ---- Quick start: 2. Generate & 3. Refine ---------------------------------

type QuickStage = {
  id: string;
  title: string;
  icon: typeof BookOpen;
  summary: string;
  note?: ReactNode;
  steps: Step[];
};

const GENERATE_STAGE: QuickStage = {
  id: "generate",
  title: "2. Generate the letter",
  icon: Sparkles,
  summary:
    "With a claim and a denial in place, the agent reads them and drafts the appeal.",
  note: (
    <>
      <strong>Precondition:</strong> the <strong>Generate letter</strong> button
      stays disabled until the session holds <strong>both</strong> a claim doc (
      <strong>837 or UB04</strong>) <em>and</em> a denial doc (
      <strong>835 or EOB</strong>). Stage 1 is exactly what gets both in.
    </>
  ),
  steps: [
    {
      text: (
        <>
          Open the <strong>Appeal Letter</strong> tab and click{" "}
          <strong>Generate letter</strong>.
        </>
      ),
      figure: {
        src: "/guide/qs3-click.png",
        alt: "Screenshot that shows the Appeal Letter tab with the Generate letter button highlighted.",
        caption: "Generate letter on the Appeal Letter tab.",
      },
    },
    {
      text: (
        <>
          A live <strong>activity feed</strong> scrolls each step (analysing
          CARC, inferring appeal type, …).
        </>
      ),
    },
    {
      text: (
        <>
          The agent may pause in a <strong>needs-document</strong> state — click{" "}
          <strong>Provide</strong> → 📎 / 🪄, then re-click{" "}
          <strong>Generate letter</strong> to resume (it does not
          auto-continue).
        </>
      ),
    },
    {
      text: (
        <>On completion the main panel auto-flips to the finished letter.</>
      ),
      figure: {
        src: "/guide/07-letter.png",
        alt: "Screenshot that shows the finished appeal letter rendered in the main panel.",
        caption: "The drafted letter, ready to refine.",
      },
    },
  ],
};

const REFINE_STAGE: QuickStage = {
  id: "refine",
  title: "3. Refine the letter",
  icon: MessageSquare,
  summary:
    "Iterate on the draft in natural language, then download it. Every change is a tracked revision.",
  steps: [
    {
      text: (
        <>
          Read the letter; flip the <strong>Letter ↔ Changes</strong> toggle
          (from v2) for a tracked-changes diff against the previous revision.
        </>
      ),
    },
    {
      text: (
        <>
          Type an instruction in the <strong>chat</strong> (or click a suggested
          chip), then <strong>Send</strong>.
        </>
      ),
      figure: {
        src: "/guide/qs4-click.png",
        alt: "Screenshot that shows the refine chat with the input box and a suggested chip highlighted.",
        caption: "Type a refinement, or pick a suggested chip.",
      },
    },
    {
      text: (
        <>
          The agent returns a new <strong>revision card</strong> in a version
          tree you can jump back through.
        </>
      ),
      figure: {
        src: "/guide/08-refine.png",
        alt: "Screenshot that shows a new revision card in the version tree.",
        caption: "Each refinement is a tracked revision.",
      },
    },
    {
      text: (
        <>
          Export <strong>PDF</strong> (instant) or <strong>DOCX</strong> (a
          short LLM conversion). For pixel edits, use <strong>Edit</strong> →
          WYSIWYG editor. Full details in <em>Reference → Letter</em> and{" "}
          <em>Reference → Refine via chat</em>.
        </>
      ),
    },
  ],
};

// ---- Reference ------------------------------------------------------------

type RefSection = NavItem & {
  intro: string;
  figure?: Figure;
  steps: ReactNode[];
  tips?: ReactNode[];
};

const REFERENCE: RefSection[] = [
  {
    id: "scenario",
    title: "Appeal-type picker",
    icon: Filter,
    intro:
      "Decide why the claim was denied — this drives which CARC codes and clinical context the generator simulates. 5 appeal types (including DRG Downgrade). Pick one, or stack several for a mixed denial.",
    figure: {
      src: "/guide/02-scenario.png",
      alt: "Appeal type picker dialog with multi-select cards and a primary badge",
      caption:
        "Appeal type picker — tick one or more cards; the first pick is the ★ primary.",
    },
    steps: [
      <>
        Open the picker by clicking the synth icon (🪄) on a denial-bearing
        row (835 / EOB) when no appeal type is set yet. The dialog shown above
        appears.
      </>,
      <>
        Tick one or more of the five appeal-type cards: <em>Medical
        Necessity</em>, <em>No Authorization</em>, <em>Coding/Bundling</em>,{" "}
        <em>Non-Covered</em>, or <em>DRG Downgrade</em>. The first one you
        pick is the <strong>★ Primary</strong> — it anchors the claim shape;
        any others are layered across the claim's lines as a{" "}
        <strong>mixed denial</strong>. Use <strong>Make primary</strong> on a
        selected card to re-anchor.
      </>,
      <>
        Click <strong>Generate with …</strong> to confirm — this locks the
        selected appeal type(s) on the session and generates the requested
        document.
      </>,
    ],
    tips: [
      <>
        The appeal-type selection is a <strong>one-way lock</strong>. To pick a
        different set, start a new session.
      </>,
      "Only denial-bearing kinds (835 / EOB) trigger this picker. 837 / UB04 / MR can be synthesized without locking an appeal type.",
      <>
        Once locked, re-opening the dialog shows a violet{" "}
        <strong>Locked to …</strong> banner and greys out the other cards —
        the set can't change without a new session.
      </>,
      <>
        The <strong>Try sample data</strong> button on the empty state opens
        this same picker in <em>bundle mode</em> — choose your appeal type(s),
        and it generates the full 6-doc sample
        (837, 835, UB04, EOB, MR, Letterhead) in one click.
      </>,
    ],
  },
  {
    id: "documents",
    title: "Documents tab",
    icon: FileText,
    intro:
      "The Documents tab on the right — manage all 15 document kinds across three groups: EDI, Letterhead, and Supporting. Each row can be uploaded or synthesized independently.",
    figure: {
      src: "/guide/03-documents.png",
      alt: "Documents tab populated with rows grouped by EDI and Supporting",
      caption:
        "Documents tab — populated rows on top, addable rows with 📎 / 🪄 actions below.",
    },
    steps: [
      <>
        Rows are grouped under <strong>EDI</strong> (835, 837),{" "}
        <strong>Letterhead</strong> (the provider header image stamped on the
        appeal letter's first page), and <strong>Supporting</strong> (UB04,
        EOB, Medical Record, Prior Auth, Operative Report, LOMN, InterQual,
        Discharge Summary, Authorization Log, Payer Policy, Timely Filing
        Proof, Other).
      </>,
      <>
        Loaded rows: green check = parsed; ✨ violet sparkle = synthetic;
        hover for ⬇ download.
      </>,
      <>
        Addable rows (dashed circle): two action icons on the right
        <ul className="list-disc pl-5 mt-1.5 space-y-1">
          <li>
            📎 <strong>Upload</strong> — real file (9 kinds support this).
          </li>
          <li>
            🪄 <strong>Synthesize</strong> — fabricate one (14 of 15 kinds
            support this; <em>Other</em> is upload-only).
          </li>
          <li>
            Disabled actions explain themselves on hover (e.g. "synthetic
            locked", "this kind isn't uploadable").
          </li>
        </ul>
      </>,
      <>
        Once Synthetic mode is engaged, a violet <strong>Synthetic mode</strong>{" "}
        callout appears at the top of the panel showing the locked appeal
        type(s).
      </>,
      <>
        Top-right <strong>Download all (.zip)</strong> bundles every document
        in the session.
      </>,
    ],
    tips: [
      "The italic mini-description under each kind helps you decide whether you need it.",
      "Multiple synth clicks queue serially — each row shows its own spinner placeholder.",
      "There is no per-row delete in this demo — New session wipes everything.",
      <>
        <strong>Why is the session locked after synthesizing?</strong>{" "}
        Synthesis writes patient, dates, CARC, etc. into the session. A
        later real upload would carry different values for the same fields,
        causing a conflict — so uploads are blocked once synthesis has
        happened. Use New session to start over with real data.
      </>,
    ],
  },
  {
    id: "viewer",
    title: "Document viewer",
    icon: BookOpen,
    intro:
      "The main panel — opens when you click any row in the Documents tab.",
    figure: {
      src: "/guide/04-viewer.png",
      alt: "Document viewer showing the Rendered / View / JSON tabs for an EDI document",
      caption: "Document viewer — tabs adapt to the document kind.",
    },
    steps: [
      "Click any row in the rail. The main panel switches to that document.",
      <>
        The header shows the filename, a violet <strong>synthetic</strong>{" "}
        badge if applicable, plus size and timestamp.
      </>,
      <>
        Switch tabs:
        <ul className="list-disc pl-5 mt-1.5 space-y-1">
          <li>
            <strong>Rendered</strong> (EDI only) — parsed DTO rendered to
            readable HTML.
          </li>
          <li>
            <strong>View</strong> — inline PDF preview.
          </li>
          <li>
            <strong>JSON</strong> — the structured parse (also present for
            synthetic PDFs; hidden for uploaded PDFs that have no structured
            parse).
          </li>
        </ul>
      </>,
      "Use the top-right ⬇ to download the single file, or 🖨 to print (PDF).",
    ],
  },
  {
    id: "fields",
    title: "Fields tab (extracted case)",
    icon: ListChecks,
    intro:
      "The Fields tab — the cross-document Case ground truth. Shows that the demo really read your documents and merged what it found.",
    figure: {
      src: "/guide/05-fields.png",
      alt: "Fields tab grouped into Patient / Claim / Provider / Payer / Authorization / Denial with code pills",
      caption: "Fields tab — code-list fields render as pills with tooltips.",
    },
    steps: [
      <>
        Switch to the <strong>Fields</strong> tab on the right.
      </>,
      <>
        Browse by group: <em>Patient</em>, <em>Claim</em>, <em>Provider</em>,{" "}
        <em>Payer</em>, <em>Authorization</em>, <em>Denial</em> — each has its
        own icon.
      </>,
      <>
        Code fields (ICD-10, CPT, CARC, RARC) render as <strong>pills</strong>{" "}
        — hover to see the official description.
      </>,
      "Multi-line fields (addresses) keep their line breaks.",
      "Each value shows which document it came from on the right.",
    ],
    tips: [
      "The Fields tab auto-refetches whenever a new document lands (extraction reruns).",
      "A field, once set, is never overwritten. This is what guarantees alignment across mixed real + synthetic documents.",
      "CARC pill colour reflects the denial category.",
    ],
  },
  {
    id: "agent",
    title: "The generation agent",
    icon: Sparkles,
    intro:
      "The Appeal Letter tab — kicks off the LLM agent. It reads the claim and the payer response, infers the appeal type, may request more documents, and drafts the letter.",
    figure: {
      src: "/guide/06-agent.png",
      alt: "Appeal Letter tab in its idle state, with the Generate letter button ready",
      caption:
        "Idle state — the agent shifts to a live activity feed once you click Generate, and may pause in a needs-document state described below.",
    },
    steps: [
      <>
        Precondition: at least one <strong>claim doc</strong> (837 or UB04)
        and one <strong>denial doc</strong> (835 or EOB). The button is
        disabled and tells you what's missing otherwise.
      </>,
      <>
        Click <strong>Generate letter</strong>.
      </>,
      <>
        The panel switches to <strong>Running</strong> — a live activity feed
        scrolls each step the agent takes (analysing CARC, inferring appeal
        type, …).
      </>,
      <>
        The agent may pause in a <strong>needs document</strong> state. You'll
        see the requested kind and the reason. Click <strong>Provide</strong>{" "}
        → 📎 Upload or 🪄 Synthesize. Once the doc lands, a fresh{" "}
        <strong>Generate letter</strong> button appears in the stream —{" "}
        <em>you</em> click it to resume (the agent does not auto-continue).
      </>,
      "On completion, the main panel auto-flips to the letter view.",
    ],
    tips: [
      "A single run usually takes 30–60 seconds. The agent can loop back into needs-document more than once — each time you re-click Generate letter to continue.",
      "Under Synthetic mode the Upload button is disabled in needs-document — only Synthesize is available.",
      "On error, click Retry to rerun the loop with the same documents.",
    ],
  },
  {
    id: "letter",
    title: "Letter — read, download & diff",
    icon: GitCompareArrows,
    intro:
      "The letter preview — version label, appeal-type badge, PDF/DOCX export, and a tracked-changes diff against the previous revision.",
    figure: {
      src: "/guide/07-letter.png",
      alt: "Letter preview with appeal-type badge, version label and Letter / Changes toggle",
      caption: "Letter preview — Changes toggle reveals tracked changes vs prior revision.",
    },
    steps: [
      "The letter is rendered in a centred iframe with print-style typography.",
      <>
        Header badges:
        <ul className="list-disc pl-5 mt-1.5 space-y-1">
          <li>
            Green <strong>Appeal type</strong> badge — what the agent inferred
            (e.g. <code>medical-necessity-inpatient</code>).
          </li>
          <li>
            Grey <strong>vN</strong> — the current revision number.
          </li>
        </ul>
      </>,
      <>
        Use the <strong>Letter ↔ Changes</strong> toggle (enabled from v2
        onwards) to flip to a tracked-changes view: red strike-through for
        removed text, green underline for added.
      </>,
      <>
        <strong>Edit</strong> — opens the WYSIWYG editor (covered in Refine via
        chat).
      </>,
      <>
        <strong>PDF</strong> downloads instantly. <strong>DOCX</strong> runs
        an LLM conversion (3–5 seconds) — the button shows a spinner.
      </>,
    ],
    tips: [
      "With only one revision the Changes toggle is disabled (nothing to compare).",
      "Manual edits via the Edit button create a new revision tagged 'Manual edit' — the original is preserved.",
    ],
  },
  {
    id: "refine-ref",
    title: "Refine via chat",
    icon: MessageSquare,
    intro:
      "The chat panel on the right — refine the letter in natural language. Every revision lands in a version tree you can jump back to.",
    figure: {
      src: "/guide/08-refine.png",
      alt: "Refine chat with user bubbles, revision cards and suggested chips",
      caption: "Refine chat — revisions appear as cards you can click to jump back to.",
    },
    steps: [
      <>
        Type an instruction in the input (Enter sends, Shift+Enter newlines).
        Or click a <strong>Suggested chip</strong> (Make tone more assertive,
        Shorten by half, …) to pre-fill it.
      </>,
      <>
        The agent responds with a <strong>revision card</strong> — vN, source
        badge (<em>Initial</em> / <em>Refined</em> / <em>Manual edit</em>),
        change summary, relative timestamp.
      </>,
      <>
        The active revision card is highlighted green with a{" "}
        <strong>✓ Current</strong> marker.
      </>,
      <>
        <strong>Click any past card</strong> to jump back to that revision —
        the head moves.
      </>,
      <>
        Hover a card for the <strong>Show changes</strong> shortcut — jumps
        straight into the Changes view for that revision vs its parent.
      </>,
      <>
        For pixel-precise edits, hit <strong>Edit</strong> in the letter
        header → WYSIWYG editor → <strong>Save</strong> creates a "Manual
        edit" revision, <strong>Cancel</strong> discards.
      </>,
    ],
    tips: [
      "Revisions form a DAG — refining after jumping back creates a fork from that revision.",
      "Chips only pre-fill the textarea — you still need to click Send.",
    ],
  },
];

// Trailing card — the reset semantics.
const RESET_CARD = {
  title: "Reset — New session",
  body: 'The "New session" button in the header wipes everything in this session: every document, the agent state, the drafted letter and the full version tree. It also clears the synthetic lock, so you can pick a different appeal type next. From the guide, New session is the canonical way back to the empty state. A confirm dialog protects against misclicks.',
};

// Sidebar groups — drive both the TOC and the scroll-spy target list.
const QUICK_NAV: NavItem[] = [
  {
    id: "prepare",
    title: "1. Prepare data",
    icon: Upload,
    children: PREPARE_METHODS.map((m) => ({
      id: m.id,
      title: m.title,
      icon: m.icon,
    })),
  },
  { id: GENERATE_STAGE.id, title: "2. Generate letter", icon: Sparkles },
  { id: REFINE_STAGE.id, title: "3. Refine letter", icon: MessageSquare },
];
const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  { label: "Quick start", items: QUICK_NAV },
  { label: "Reference", items: REFERENCE },
];
// Flatten (including children) for the scroll-spy observer.
const ALL_IDS = NAV_GROUPS.flatMap((g) =>
  g.items.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) ?? [])]),
);

// ---------------------------------------------------------------------------
// Lightbox — every figure is click-to-zoom.
// ---------------------------------------------------------------------------

const LightboxCtx = createContext<(f: Figure) => void>(() => {});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function GuidePage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string>(ALL_IDS[0]);
  const [zoom, setZoom] = useState<Figure | null>(null);

  // Reset to the first section on mount, so re-opening the guide from the
  // header doesn't strand the user mid-scroll.
  useEffect(() => {
    setActiveId(ALL_IDS[0]);
    scrollRef.current?.scrollTo({ top: 0 });
  }, []);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Highlight whichever section is closest to the top of the scroll viewport.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const targets = ALL_IDS.map((id) =>
      root.querySelector<HTMLElement>(`#guide-${id}`),
    ).filter((el): el is HTMLElement => !!el);
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id.replace(/^guide-/, ""));
        }
      },
      { root, rootMargin: "0px 0px -65% 0px", threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  function jumpTo(id: string) {
    const root = scrollRef.current;
    const target = root?.querySelector<HTMLElement>(`#guide-${id}`);
    if (!root || !target) return;
    const rootRect = root.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const newTop = root.scrollTop + (targetRect.top - rootRect.top) - 8;
    root.scrollTo({ top: newTop, behavior: "smooth" });
    setActiveId(id);
  }

  return (
    <LightboxCtx.Provider value={setZoom}>
      <main
        className="grid bg-white"
        style={{
          gridTemplateColumns: "260px 1fr",
          height: "calc(100vh - var(--chrome-h, 56px))",
        }}
      >
        {/* Sidebar TOC */}
        <aside className="border-r bg-slate-50/60 flex flex-col min-h-0">
          <div className="px-4 py-4 border-b">
            <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
              <BookOpen className="w-4 h-4 text-violet-600" />
              User guide
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
              Quick start first, then a per-feature reference.
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto p-2">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-2 last:mb-0">
                <div className="px-2.5 pt-2 pb-1 text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
                  {group.label}
                </div>
                <ul className="space-y-0.5">
                  {group.items.map((s) => (
                    <li key={s.id}>
                      <NavButton item={s} activeId={activeId} onJump={jumpTo} />
                      {s.children && (
                        <ul className="mt-0.5 ml-3.5 pl-2 border-l border-slate-200 space-y-0.5">
                          {s.children.map((c) => (
                            <li key={c.id}>
                              <NavButton
                                item={c}
                                activeId={activeId}
                                onJump={jumpTo}
                                small
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-200/70 px-2.5 text-[11px] text-muted-foreground leading-snug">
              <div className="font-medium text-slate-600 mb-0.5">Reset</div>
              Use <strong>New session</strong> in the header to wipe everything
              and return to the empty state.
            </div>
          </nav>
        </aside>

        {/* Content */}
        <div className="flex flex-col min-h-0 relative">
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto px-10 py-8 scroll-smooth"
          >
            <header className="mb-8 max-w-[720px]">
              <h1 className="text-2xl font-semibold tracking-tight">
                Appeal Letter Generation Demo
              </h1>
              <p className="mt-2 text-sm text-muted-foreground leading-6">
                Start with the three-stage <strong>Quick start</strong> to go
                from an empty screen to a finished letter, then dip into the{" "}
                <strong>Reference</strong> for any panel in depth. Click any
                screenshot to enlarge it.
              </p>
            </header>

            {/* Quick start */}
            <GroupHeading
              icon={Rocket}
              label="Quick start"
              blurb="The linear happy path: prepare data → generate → refine. Do the stages in order — each one explains what unlocks the next."
            />
            <div className="mb-8 max-w-[760px] rounded-lg border border-violet-200 bg-violet-50/60 px-5 py-4">
              <div className="text-[12px] uppercase tracking-wider font-semibold text-violet-800 mb-1.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                The one rule that trips people up
              </div>
              <p className="text-[13px] leading-6 text-violet-950">
                <strong>Generate letter</strong> needs <strong>two</strong>{" "}
                documents: a <strong>claim</strong> (837 or UB04) <em>and</em>{" "}
                the <strong>denial</strong> it appeals (835 or EOB). That is the
                whole job of <strong>Stage 1 · Prepare data</strong>. Picking
                the appeal type isn't a separate step — it's how the denial gets
                created.
              </p>
            </div>

            {/* Stage 1 — Prepare data */}
            <StageHeading id="prepare" n="1" title="Prepare data">
              Get a <strong>claim</strong> and a <strong>denial</strong> into
              the session. Three ways — pick whichever matches what you have.
              The <em>Upload</em> and <em>Synthesize</em> paths create the
              denial (and so the appeal-type lock) from the Documents tab;{" "}
              <em>Try sample data</em> does it all in one click.
            </StageHeading>
            <div className="space-y-8 mb-12">
              {PREPARE_METHODS.map((m) => (
                <PrepMethodBlock key={m.id} method={m} />
              ))}
            </div>

            {/* Stage 2 — Generate */}
            <QuickStageBlock stage={GENERATE_STAGE} />
            {/* Stage 3 — Refine */}
            <QuickStageBlock stage={REFINE_STAGE} />

            {/* Reference */}
            <GroupHeading
              icon={BookOpen}
              label="Reference"
              blurb="Every panel and control, explained on its own. Browse in any order — this is not a sequence."
              className="mt-12"
            />
            {REFERENCE.map((s) => (
              <Section key={s.id} section={s} />
            ))}

            {/* Reset trailer */}
            <section className="mt-6 border-t pt-6 max-w-[840px]">
              <h2 className="text-[15px] font-semibold tracking-tight flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                {RESET_CARD.title}
              </h2>
              <p className="mt-2 text-[13px] text-muted-foreground leading-6">
                {RESET_CARD.body}
              </p>
            </section>

            <footer className="mt-12 pt-6 border-t text-[11px] text-muted-foreground flex items-center gap-2">
              <Send className="w-3 h-3" />
              Found something unclear? File feedback on the demo repo.
            </footer>
          </div>
        </div>
      </main>

      {zoom && <Lightbox figure={zoom} onClose={() => setZoom(null)} />}
    </LightboxCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

function NavButton({
  item,
  activeId,
  onJump,
  small,
}: {
  item: NavItem;
  activeId: string;
  onJump: (id: string) => void;
  small?: boolean;
}) {
  const Icon = item.icon;
  const active =
    item.id === activeId ||
    (item.children?.some((c) => c.id === activeId) ?? false);
  return (
    <button
      type="button"
      onClick={() => onJump(item.id)}
      className={cn(
        "w-full flex items-center gap-2 px-2.5 rounded-md text-left transition-colors",
        small ? "py-1 text-[12px]" : "py-1.5 text-[12.5px]",
        active
          ? "bg-white border border-slate-200 text-foreground font-medium shadow-sm"
          : "text-muted-foreground hover:bg-white/70 hover:text-foreground",
      )}
    >
      <Icon
        className={cn(
          "shrink-0",
          small ? "w-3 h-3" : "w-3.5 h-3.5",
          active ? "text-violet-600" : "text-slate-400",
        )}
      />
      <span className="truncate">{item.title}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Headings
// ---------------------------------------------------------------------------

function GroupHeading({
  icon: Icon,
  label,
  blurb,
  className,
}: {
  icon: typeof BookOpen;
  label: string;
  blurb: string;
  className?: string;
}) {
  return (
    <div className={cn("max-w-[760px] mb-5", className)}>
      <h2 className="text-[12px] uppercase tracking-[0.14em] font-semibold text-violet-700 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </h2>
      <p className="mt-1.5 text-[13px] text-muted-foreground leading-6">
        {blurb}
      </p>
    </div>
  );
}

function StageHeading({
  id,
  n,
  title,
  children,
}: {
  id: string;
  n: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div id={`guide-${id}`} className="scroll-mt-4 max-w-[760px] mb-4">
      <h3 className="text-[17px] font-semibold tracking-tight flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-[13px] font-semibold tabular-nums">
          {n}
        </span>
        {title}
      </h3>
      <p className="mt-1.5 text-[13px] text-muted-foreground leading-6">
        {children}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick-start renderers — Microsoft Learn style: numbered steps, one inline
// screenshot under the step it illustrates.
// ---------------------------------------------------------------------------

function PrepMethodBlock({ method }: { method: PrepMethod }) {
  const Icon = method.icon;
  return (
    <section id={`guide-${method.id}`} className="scroll-mt-4 max-w-[760px]">
      <h4 className="text-[15px] font-semibold tracking-tight flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-50 text-violet-700 shrink-0">
          <Icon className="w-3.5 h-3.5" />
        </span>
        {method.title}
      </h4>
      <p className="mt-1 ml-8 text-[12.5px] text-muted-foreground leading-snug">
        {method.tagline}
      </p>
      <StepList steps={method.steps} />
    </section>
  );
}

function QuickStageBlock({ stage }: { stage: QuickStage }) {
  const Icon = stage.icon;
  return (
    <section id={`guide-${stage.id}`} className="scroll-mt-4 max-w-[760px] mb-12">
      <h3 className="text-[17px] font-semibold tracking-tight flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-violet-50 text-violet-700">
          <Icon className="w-4 h-4" />
        </span>
        {stage.title}
      </h3>
      <p className="mt-1.5 text-[13px] text-muted-foreground leading-6">
        {stage.summary}
      </p>
      {stage.note && <NoteBlock>{stage.note}</NoteBlock>}
      <StepList steps={stage.steps} />
    </section>
  );
}

// Numbered procedure. Each step renders its text, then — if present — the
// single screenshot that illustrates that exact step, full column width.
function StepList({ steps }: { steps: Step[] }) {
  return (
    <ol className="mt-4 space-y-4">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <span
            aria-hidden
            className="shrink-0 mt-[1px] inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-semibold tabular-nums"
          >
            {i + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] leading-6">{step.text}</div>
            {step.figure && (
              <Figure figure={step.figure} className="mt-2.5" halfScale />
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function NoteBlock({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 rounded-md border-l-4 border-violet-400 bg-violet-50/60 pl-3 pr-4 py-2.5">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 mb-0.5 flex items-center gap-1.5">
        <Info className="w-3 h-3" />
        Note
      </div>
      <p className="text-[12.5px] leading-6 text-violet-950/90">{children}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reference renderers
// ---------------------------------------------------------------------------

function Section({ section }: { section: RefSection }) {
  const Icon = section.icon;
  return (
    <section
      id={`guide-${section.id}`}
      className="scroll-mt-4 max-w-[840px] mb-12 last:mb-4"
    >
      <h2 className="text-[18px] font-semibold tracking-tight flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-violet-50 text-violet-700">
          <Icon className="w-3.5 h-3.5" />
        </span>
        {section.title}
      </h2>
      <p className="mt-2 text-[13px] text-muted-foreground leading-6">
        {section.intro}
      </p>

      {section.figure && <Figure figure={section.figure} className="mt-4" halfScale />}

      <h3 className="mt-5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        How to use
      </h3>
      <ol className="mt-2 space-y-2 text-[13px] leading-6">
        {section.steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span
              aria-hidden
              className="shrink-0 mt-[2px] inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-semibold tabular-nums"
            >
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">{step}</div>
          </li>
        ))}
      </ol>

      {section.tips && section.tips.length > 0 && (
        <div className="mt-5 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Tips & gotchas
          </div>
          <ul className="space-y-1.5 text-[12.5px] leading-5 text-amber-950/90">
            {section.tips.map((tip, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-600 shrink-0">•</span>
                <span className="flex-1 min-w-0">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Figure — a single bordered, click-to-zoom screenshot at readable width.
// One consistent renderer for both Quick start steps and Reference sections.
// ---------------------------------------------------------------------------

function Figure({
  figure,
  className,
  halfScale,
}: {
  figure: Figure;
  className?: string;
  halfScale?: boolean;
}) {
  const open = useContext(LightboxCtx);
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={cn(
          "rounded-lg border bg-slate-50 min-h-[100px] flex items-center justify-center text-[11.5px] text-muted-foreground italic px-4 text-center",
          className,
        )}
      >
        Screenshot pending — run{" "}
        <code className="mx-1">scripts/capture_guide_screenshots.mjs</code>.
      </div>
    );
  }
  return (
    <figure
      className={cn(
        "rounded-lg border bg-slate-50 overflow-hidden inline-block max-w-full",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => open(figure)}
        className="block cursor-zoom-in"
        title="Click to enlarge"
      >
        <img
          src={figure.src}
          alt={figure.alt}
          loading="lazy"
          onLoad={(e) => {
            // Screenshots are captured at deviceScaleFactor=2. Display at half
            // natural width (capped to the column) so text stays crisp and
            // readable inline without upscaling.
            const img = e.currentTarget;
            if (halfScale && img.naturalWidth > 0) {
              img.style.width = `${img.naturalWidth / 2}px`;
            }
          }}
          onError={() => setFailed(true)}
          className="block max-w-full h-auto"
        />
      </button>
      {figure.caption && (
        <figcaption className="px-3 py-1.5 text-[11.5px] text-muted-foreground border-t bg-white">
          {figure.caption}
        </figcaption>
      )}
    </figure>
  );
}

function Lightbox({ figure, onClose }: { figure: Figure; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <X className="w-5 h-5" />
      </button>
      <figure
        className="max-w-[92vw] max-h-[92vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={figure.src}
          alt={figure.alt}
          className="max-w-[92vw] max-h-[84vh] object-contain rounded-lg shadow-2xl bg-white"
        />
        {figure.caption && (
          <figcaption className="mt-3 text-center text-[12.5px] text-white/85 max-w-[80vw]">
            {figure.caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
